'use client';

import { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import FullscreenIcon from '@mui/icons-material/Fullscreen';

interface VideoHeroProps {
  /** Path to the video file (served from /public) */
  src: string;
  /** Optional poster image shown before playback starts */
  poster?: string;
  /** CSS aspect-ratio value (e.g. '16 / 9'); defaults to '16 / 9' */
  aspectRatio?: string;
}

/**
 * Autoplaying muted marketing video with overlay controls.
 * Tapping the video (or the fullscreen button) unmutes and enters fullscreen.
 */
export function VideoHero({ src, poster, aspectRatio = '16 / 9' }: VideoHeroProps) {
  const t = useTranslations('creator.landing.videoHero');
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  }, []);

  const enterFullscreen = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = false;
    setIsMuted(false);
    videoRef.current.requestFullscreen?.();
  }, []);

  const overlayButtonSx = {
    bgcolor: alpha(theme.palette.common.black, 0.55),
    color: theme.palette.common.white,
    '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.75) },
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        boxShadow: 2,
        position: 'relative',
        aspectRatio,
        width: '100%',
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        onClick={enterFullscreen}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          cursor: 'pointer',
        }}
        aria-label={t('ariaLabel')}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          gap: 0.5,
        }}
      >
        <IconButton
          onClick={toggleMute}
          aria-label={isMuted ? t('unmute') : t('mute')}
          size="small"
          sx={overlayButtonSx}
        >
          {isMuted ? (
            <VolumeOffIcon fontSize="small" />
          ) : (
            <VolumeUpIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          onClick={enterFullscreen}
          aria-label={t('fullscreen')}
          size="small"
          sx={overlayButtonSx}
        >
          <FullscreenIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
